import setCustomShippingDetails from "@iewc/Store/Handlers/CheckoutReviewAndSubmit/CustomSetShippingDetails";
import { CustomState } from "@iewc/Store/Reducers";
import { typography } from "@iewc/Utilities/styleguide/Typography";
import { formatDateToMonthDay } from "@iewc/Utilities/Utilities";
import { colorStrings, mediaQueriesBP } from "@iewc/Utilities/variables";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";

import addDays from "@insite/client-framework/Common/Utilities/addDays";
import {
    convertDateOnlyStringToDate,
    convertDateToDateOnlyString,
} from "@insite/client-framework/Common/Utilities/DateUtilities";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetIsPreloadingData";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedDeliveryDate";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedPickUpDate";
import setShippingMethod from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetShippingMethod";
import translate from "@insite/client-framework/Translate";
import { CarrierDto, ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import baseTheme from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Info from "@insite/mobius/Icons/Info";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Select from "@insite/mobius/Select";
import { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import FieldSetPresentationProps from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export type CustomCatalogSetting = {
    [key: string]: string; // 👈️ variable key
};

interface OwnProps extends WidgetProps {}

interface CalendarSettingsProps {
    showOnLink: boolean;
    onLink: string;
    onLinkDescription: string;
    showOffLink: boolean;
    offLink: string;
    offLinkDescription: string;
}

const mapStateToProps = (state: CustomState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const settingsCollection = getSettingsCollection(state);
    const calendarSettings = settingsCollection.calendarSettings as CalendarSettingsProps;

    const {
        onCalendar,
        estimateShipDateOrderChanged,
        cartLines: customCartLines,
        cartEstimateShipDate,
    } = state.customReviewAndSubmit;

    return {
        cart: cartState.value,
        cartId,
        session: state.context.session,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        settingsCollection,
        calendarSettings,
        shipToState: getShipToState(state, cartState.value ? cartState.value.shipToId : undefined),
        // Verndale Customization
        onCalendar,
        estimateShipDateOrderChanged,
        customCartLines,
        cartEstimateShipDate,
    };
};

const mapDispatchToProps = {
    setShippingMethod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    setIsPreloadingData,
    setCustomShippingDetails,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutReviewAndSubmitShippingDetailsStyles {
    form?: InjectableCss;
    fieldset?: InjectableCss;
    shipDetailsHeading?: TypographyPresentationProps;
    shipMethodItemTitle?: TypographyPresentationProps;
    shipMethodItemCopy?: TypographyPresentationProps;
    shipMethodContainer?: GridContainerProps;
    shipMethodItem?: GridItemProps;
    loadingSpinner?: LoadingSpinnerProps;
    deliveryDatePicker?: DatePickerPresentationProps;
    serviceTypeRadioGroup?: RadioGroupProps;
    serviceTypeRadioButton?: FieldSetPresentationProps<RadioComponentProps>;
    tooltipStyles?: TooltipPresentationProps;
    tooltipTrigger?: InjectableCss;
    triggerText?: TypographyProps;
    availabilityTooltipWrapper?: InjectableCss;
    availabilityTooltipTitle?: TypographyProps;
    availabilityTooltipText?: TypographyProps;
    availabilityTooltipLink?: LinkPresentationProps;
    innerItemContainer?: GridContainerProps;
    innerItem?: GridItemProps;
    infoIcon?: IconProps;
    seeOrderLinesLabel?: TypographyProps;
    shippingScheduleLabel?: TypographyProps;
    loadingWrapper?: InjectableCss;
    invalidAddress?: TypographyPresentationProps;
}

export const checkoutReviewAndSubmitShippingDetailsStyles: CheckoutReviewAndSubmitShippingDetailsStyles = {
    fieldset: {
        css: css`
            margin: 15px 0;
            padding: 0;
            border: 0;
        `,
    },
    shipDetailsHeading: { variant: "h5" },
    shipMethodContainer: {
        gap: 10,
    },
    shipMethodItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    loadingSpinner: {
        css: css`
            margin: auto;
        `,
    },
    shipMethodItemTitle: {
        weight: "bold",
        transform: "uppercase",
        size: 14,
    },
    shipMethodItemCopy: {
        size: 14,
    },
    serviceTypeRadioGroup: {
        css: css`
            width: 100%;
        `,
    },
    serviceTypeRadioButton: {
        css: css`
            & > input {
                position: absolute;
            }

            & > label {
                display: flex;
                justify-content: flex-start;
                align-items: center;
                width: 100%;

                & > span.description {
                    flex-grow: 1;
                }

                & > span.price {
                    font-weight: bold;
                    margin-left: auto;
                }
            }

            input + label {
                margin: 0;
            }
        `,
    },

    innerItemContainer: {
        gap: 0,
        offsetCss: css`
            flex-direction: column;
            gap: 15px;
            justify-content: flex-start;
        `,
    },

    innerItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            &.display-calendar-tooltip {
                position: relative;
            }

            &.delivery-date-picker {
                position: relative;
                display: block;
            }
        `,
    },

    triggerText: {
        size: 14,
        css: css`
            cursor: pointer;
            text-decoration: underline;
            text-decoration-style: dashed;
            text-underline-offset: 5px;
        `,
    },

    availabilityTooltipWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 10px;
            align-items: flex-start;
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            text-align: left;
            bottom: 30px;
            position: absolute;
            width: 100%;
            max-width: 300px;
            z-index: 2;
            padding: 20px 30px;
            border: 0;
            border-radius: 0;
            left: 5%;
            box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);

            &::before {
                content: "";
                position: absolute;
                top: 100%;
                left: 10%;
                margin-left: -10px;
                border: 10px solid transparent;
                border-top-color: ${getColor(colorStrings.commonGlobalWhite)};
            }
        `,
    },

    availabilityTooltipTitle: {
        ...typography.h3,
        variant: "h3",
        color: colorStrings.textMain,
        size: "29px",
        lineHeight: "35px",
        css: css`
            ${baseTheme.typography.h3.css}
            margin:0;
            text-transform: uppercase;
            ${mediaQueriesBP.maxMD} {
                font-size: 24px;
                line-height: 30px;
            }
        `,
    },

    availabilityTooltipText: {
        size: 14,
    },

    tooltipStyles: {
        typographyProps: {},
        cssOverrides: {
            tooltipWrapper: css`
                margin-left: 5px;
            `,
            tooltipContainer: css`
                width: auto;
                min-width: 300px;
                margin-left: 50px;
                &::after {
                    left: 50px;
                }
            `,
            tooltipBody: css`
                padding: 10px;
            `,
        },
    },

    infoIcon: {
        src: Info,
        size: 14,
        color: colorStrings.primaryMain,
        css: css`
            margin-right: 5px;
        `,
    },
    seeOrderLinesLabel: {
        size: 14,
        as: "p",
        css: css`
            position: absolute;
            top: 50%;
            left: 10px;
            height: 35px;
            line-height: 35px;
            background-color: ${getColor(colorStrings.commonBackground)};
            width: 84%;
            z-index: 2;
        `,
    },

    deliveryDatePicker: {
        cssOverrides: {
            formField: css`
                font-size: 14px;

                label {
                    font-size: 14px;
                }
            `,
        },
    },

    shippingScheduleLabel: {
        size: 14,
        weight: "bold",
        css: css`
            display: flex;
            align-items: center;
            margin-bottom: 10px;
        `,
    },

    loadingWrapper: {
        css: css`
            align-items: center;
            display: inline-flex;
            height: 100%;
            justify-content: center;
            min-height: 150px;
            width: 100%;
        `,
    },

    invalidAddress: {
        variant: "legend",
        forwardAs: "span",
    },
};

const CheckoutReviewAndSubmitShippingDetails = ({
    cart,
    shipToState,
    currentUserIsGuest,
    settingsCollection,
    calendarSettings,
    setShippingMethod,
    setRequestedDeliveryDate,
    setIsPreloadingData,
    // Verndale Customization
    setCustomShippingDetails,
    onCalendar,
    estimateShipDateOrderChanged,
    customCartLines,
    cartEstimateShipDate,
}: Props) => {
    const styles = checkoutReviewAndSubmitShippingDetailsStyles;
    const StyledForm = getStyledWrapper("form");
    const StyledFieldSet = getStyledWrapper("fieldset");
    const [optionDescription, setOptionDescription] = useState("");
    const [multipleShipmentMessageText, setMultipleShipmentMessageText] = useState("");
    const [initialShipDay, setInitialShipDay] = useState<Date | null>(null);

    const [selectedCarrier, setSelectedCarrier] = useState<CarrierDto | null>(null);
    const [selectedShipVia, setSelectedShipVia] = useState<ShipViaDto | null>(null);
    const [customShippingOptions, setCustomShippingOptions] = useState<string[]>([]);
    const [showCalendarOnOff, setShowCalendarOnOff] = useState(false);
    const [onCalendarSelected, setOnCalendarSelected] = useState(false);
    const [displayCalendarTooltip, setDisplayCalendarTooltip] = useState(false);
    const [datePickerLabel, setDatePickerLabel] = useState(translate("Please select a preferred date"));
    const tooltipRef = useRef<HTMLDivElement | null>(null);

    const [estimatedShippingValidDatesArr, setEstimatedShippingValidDatesArr] = useState<string[]>([]);
    const [dayCalendarClicked, setDayCalendarClicked] = useState(false);

    const [isLoading, setIsLoading] = useState(true);

    const clearTimeOut = () => {
        setTimeout(() => {
            setIsLoading(false);
        }, 200);
    };

    useEffect(() => {
        if (cart) {
            setIsLoading(true);
            const {
                properties: { optionDescription, shippingOptions, onCalendarShippingDate },
                requestedDeliveryDate,
            } = cart;

            let deliveryDate = requestedDeliveryDate ?? new Date().toISOString().split("T")[0];

            if (shipToState.value) {
                const {
                    properties: { onCalendar },
                } = shipToState.value;

                if (onCalendar != null && (onCalendar.toLowerCase() === "y" || onCalendar.toLowerCase() === "yes")) {
                    // we get the min date from the estimatedShippingValidDatesArr

                    setShowCalendarOnOff(true);
                    setOnCalendarSelected(true);
                    onCalendarCalculateEstimatedShippingDates();
                } else {
                    deliveryDate = convertDateToDateOnlyString(addDays(new Date(), 1));

                    const customCartLines = cart?.cartLines?.map(cartLine => {
                        return {
                            id: cartLine.id,
                            estimateShipDate: deliveryDate,
                        };
                    });

                    setCustomShippingDetails({
                        onCalendar: false,
                        cartEstimateShipDate: deliveryDate,
                        estimateShipDateOrderChanged: false,
                        cartLines: customCartLines ?? [],
                    });
                    setDatePickerLabel(`${translate("Please select a preferred date")}`);
                    setInitialShipDay(convertDateOnlyStringToDate(deliveryDate) as Date);
                    clearTimeOut();
                }
            }

            setOptionDescription(optionDescription ?? "");
            setSelectedCarrier(cart.carrier);
            setSelectedShipVia(cart.shipVia);
            setRequestedDeliveryDate({
                requestedDeliveryDate: convertDateOnlyStringToDate(onCalendarShippingDate),
                requestedDeliveryDateDisabled: false,
            });

            if (shippingOptions && typeof shippingOptions === "string" && shippingOptions.length > 0) {
                setCustomShippingOptions(shippingOptions.split("~").filter(o => o.length > 0));
            }
        }
    }, [cart]);

    useEffect(() => {
        if (settingsCollection) {
            const { customCatalogSettings } = settingsCollection;

            if (customCatalogSettings) {
                setMultipleShipmentMessageText(
                    (customCatalogSettings as CustomCatalogSetting).multipleShipmentMessageText.toLowerCase() ?? "",
                );
            }
        }
    }, [settingsCollection]);

    useEffect(() => {
        if (cart) {
            setIsLoading(true);

            const { requestedDeliveryDate } = cart;

            if (onCalendarSelected) {
                onCalendarCalculateEstimatedShippingDates(onCalendarSelected);
            } else {
                const offCalendar = addDays(new Date(), 1);
                const deliveryDate = requestedDeliveryDate ?? offCalendar.toISOString().split("T")[0];

                setCustomShippingDetails({
                    onCalendar: false,
                    cartEstimateShipDate: deliveryDate,
                    estimateShipDateOrderChanged: false,
                    cartLines: customCartLines.map(cartLine => {
                        return {
                            id: cartLine.id,
                            estimateShipDate: deliveryDate,
                        };
                    }),
                });
                setDatePickerLabel(`${translate("Please select a preferred date")}`);
                setInitialShipDay(convertDateOnlyStringToDate(deliveryDate) as Date);
                clearTimeOut();
            }
        }
    }, [onCalendarSelected]);

    const handleClickOutsideTooltip = (e: MouseEvent) => {
        if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node) && displayCalendarTooltip) {
            setDisplayCalendarTooltip(false);
        }
    };

    useEffect(() => {
        window.addEventListener("mousedown", handleClickOutsideTooltip);
        return () => window.removeEventListener("mousedown", handleClickOutsideTooltip);
    });

    useEffect(() => {
        if (estimateShipDateOrderChanged) {
            onCalendarCalculateEstimatedShippingDates(estimateShipDateOrderChanged);
        }
    }, [estimateShipDateOrderChanged, customCartLines]);

    const handleRequestDeliveryDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        if (
            onCalendarSelected &&
            (!dayCalendarClicked || (dayCalendarClicked && estimateShipDateOrderChanged)) &&
            !currentUserIsGuest
        ) {
            return;
        }

        setDayCalendarClicked(false);

        const customCartLines = cart?.cartLines?.map(cartLine => {
            return {
                id: cartLine.id,
                estimateShipDate: convertDateToDateOnlyString(selectedDay),
            };
        });

        setCustomShippingDetails({
            onCalendar,
            estimateShipDateOrderChanged: false,
            cartLines: customCartLines ?? [],
            cartEstimateShipDate: convertDateToDateOnlyString(selectedDay),
        });

        setRequestedDeliveryDate({
            requestedDeliveryDate:
                typeof selectedDay === "string" ? convertDateOnlyStringToDate(selectedDay) : selectedDay,
            requestedDeliveryDateDisabled: selectedDayDisabled,
        });

        if (onCalendarSelected) {
            setDatePickerLabel(
                `${translate("Estimated Shipping Date")}: ${formatDateToMonthDay(
                    convertDateToDateOnlyString(selectedDay),
                )}`,
            );
        }
    };

    const shipViaChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (!selectedCarrier) {
            return;
        }
        const selectedShipVia = selectedCarrier.shipVias!.find(s => s.id === event.currentTarget.value)!;
        setIsPreloadingData({ isPreloadingData: true });
        setSelectedShipVia(selectedShipVia);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia: selectedShipVia,
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };

    const onCalendarCalculateEstimatedShippingDates = (onCalendar?: boolean) => {
        if (cart) {
            const {
                properties: { onCalendarShippingDate, estimatedShippingValidDates },
            } = cart;

            let deliveryDate = onCalendarShippingDate;

            if (estimatedShippingValidDates && estimatedShippingValidDates.length > 0) {
                const validDates = estimatedShippingValidDates.split(",");
                setEstimatedShippingValidDatesArr(validDates);
                deliveryDate = validDates.reduce((prev, curr) => {
                    return prev <= curr ? prev : curr;
                });
            }

            // if all cart lines have the same calendar shipping date or user changed on top the date,
            // then set the setDatePickerLabel to that date
            // else, set the setDatePickerLabel to between the min and max calendar shipping dates

            if (
                customCartLines?.every(line => line.estimateShipDate === onCalendarShippingDate) ||
                !estimateShipDateOrderChanged
            ) {
                setDatePickerLabel(`${translate("Estimated Shipping Date")}: ${formatDateToMonthDay(deliveryDate)}`);
                setRequestedDeliveryDate({
                    requestedDeliveryDate: convertDateOnlyStringToDate(deliveryDate),
                });
                setCustomShippingDetails({
                    onCalendar,
                    cartEstimateShipDate: deliveryDate,
                    estimateShipDateOrderChanged: false,
                    cartLines: customCartLines.map(cartLine => {
                        return {
                            id: cartLine.id,
                            estimateShipDate: deliveryDate,
                        };
                    }),
                });
                setOptionDescription(optionDescription ?? "");
            } else {
                let minDate;
                let maxDate;

                if (customCartLines && customCartLines.length >= 2) {
                    const minCartLine = customCartLines.reduce((prev: any, curr: any) => {
                        return prev.estimateShipDate <= curr.estimateShipDate ? prev : curr;
                    }, customCartLines[0]);
                    minDate = minCartLine ? minCartLine.estimateShipDate : undefined;

                    const maxCartLine = customCartLines.reduce((prev: any, curr: any) => {
                        return prev.estimateShipDate > curr.estimateShipDate ? prev : curr;
                    }, customCartLines[0]);
                    maxDate = maxCartLine ? maxCartLine.estimateShipDate : undefined;
                } else if (customCartLines && customCartLines.length === 1) {
                    minDate = maxDate = customCartLines[0].estimateShipDate;
                }

                if (minDate !== maxDate) {
                    setDatePickerLabel(
                        `${translate("Estimated Shipping Date")}: 
                    ${formatDateToMonthDay(minDate ?? "")} 
                    - ${formatDateToMonthDay(maxDate ?? "")}`,
                    );

                    deliveryDate = minDate;

                    const multipleDescription = "multiple shipments";
                    setOptionDescription(multipleDescription ?? "");
                } else {
                    setDatePickerLabel(
                        `${translate("Estimated Shipping Date")}: 
                 ${formatDateToMonthDay(maxDate ?? "")}`,
                    );
                    deliveryDate = maxDate;

                    const singleShipment = "single shipment";
                    setOptionDescription(singleShipment ?? "");
                }

                setCustomShippingDetails({
                    onCalendar,
                    cartEstimateShipDate: deliveryDate,
                    estimateShipDateOrderChanged: true,
                    cartLines: customCartLines,
                });

                setRequestedDeliveryDate({
                    requestedDeliveryDate: convertDateOnlyStringToDate(minDate ?? ""),
                });
            }

            setInitialShipDay(convertDateOnlyStringToDate(deliveryDate) as Date);
            clearTimeOut();
        }
    };

    if (currentUserIsGuest) {
        return (
            <StyledForm
                {...styles.form}
                id="checkoutReviewAndSubmitShippingDetailsForm"
                // onSubmit={(event: React.FormEvent<HTMLFormElement>) => event.preventDefault()}
                noValidate={true}
                data-test-selector="checkoutReviewAndSubmitShippingDetailsForm"
            >
                <StyledFieldSet {...styles.fieldset}>
                    <GridContainer {...styles.shipMethodContainer}>
                        <GridItem {...styles.shipMethodItem}>
                            <Typography {...styles.shipDetailsHeading} as="h2">
                                {translate("Shipping Details")}
                            </Typography>
                            <Typography {...styles.shipMethodItemTitle}>{optionDescription}</Typography>
                            {optionDescription.toLowerCase() !== multipleShipmentMessageText.toLowerCase() ? (
                                <Typography {...styles.shipMethodItemCopy}>
                                    {siteMessage("ReviewAndPay_ShipCompleteMessage")}
                                </Typography>
                            ) : (
                                <Typography {...styles.shipMethodItemCopy}>
                                    {siteMessage("ReviewAndPay_ShipAsAvailableMessage")}
                                </Typography>
                            )}
                        </GridItem>
                        <GridItem {...styles.shipMethodItem}>
                            <Typography {...styles.shipDetailsHeading} as="h2">
                                {translate("Preferred Delivery Date (Optional)")}
                            </Typography>
                            {initialShipDay && (
                                <DatePicker
                                    label={datePickerLabel}
                                    hint={siteMessage("ReviewAndPay_PreferredDeliveryDateMessageGuest")}
                                    {...styles.deliveryDatePicker}
                                    selectedDay={initialShipDay}
                                    dateTimePickerProps={{
                                        maxDate: addDays(
                                            initialShipDay,
                                            settingsCollection.cartSettings.maximumDeliveryPeriod,
                                        ),
                                        monthAriaLabel: translate("Request delivery date month"),
                                        dayAriaLabel: translate("Request delivery date day"),
                                        yearAriaLabel: translate("Request delivery date year"),
                                        tileDisabled: (tile: { date: Date | undefined }) => {
                                            if (tile.date && tile.date < initialShipDay) {
                                                return true;
                                            }
                                            return false;
                                        },
                                        ...styles.deliveryDatePicker?.dateTimePickerProps,
                                    }}
                                    onDayChange={(e: DatePickerState) => handleRequestDeliveryDateChanged(e)}
                                />
                            )}

                            <br />
                            <RadioGroup
                                {...styles.serviceTypeRadioGroup}
                                label={translate("Select Service")}
                                value={selectedShipVia?.id ?? ""}
                                onChangeHandler={shipViaChangeHandler}
                                data-test-selector="checkoutReviewAndSubmit_shippingServiceSelect"
                            >
                                {customShippingOptions.length > 0 &&
                                    customShippingOptions.map(option => {
                                        const optionFormat = option.split("|");

                                        return (
                                            <Radio
                                                {...styles.serviceTypeRadioButton}
                                                value={optionFormat[0]}
                                                key={optionFormat[0]}
                                            >
                                                <span className="description">{optionFormat[1]} </span>
                                                <span className="price">{optionFormat[2]}</span>
                                            </Radio>
                                        );
                                    })}
                            </RadioGroup>

                            {(cart?.carriers?.length ?? 0) === 0 && (
                                <Typography {...styles.invalidAddress}>
                                    {siteMessage("ReviewAndPay_InvalidAddressToShowCarrierServices")}
                                </Typography>
                            )}
                        </GridItem>
                    </GridContainer>
                </StyledFieldSet>
            </StyledForm>
        );
    }

    return (
        <StyledForm
            {...styles.form}
            id="checkoutReviewAndSubmitShippingDetailsForm"
            // onSubmit={(event: React.FormEvent<HTMLFormElement>) => event.preventDefault()}
            noValidate={true}
            data-test-selector="checkoutReviewAndSubmitShippingDetailsForm"
        >
            <StyledFieldSet {...styles.fieldset}>
                <GridContainer {...styles.shipMethodContainer}>
                    <GridItem {...styles.shipMethodItem}>
                        <GridContainer {...styles.innerItemContainer}>
                            <GridItem {...styles.innerItem}>
                                <Typography {...styles.shipDetailsHeading} as="h2">
                                    {translate("Shipping Details")}
                                </Typography>
                            </GridItem>
                            {showCalendarOnOff && (
                                <>
                                    <GridItem {...styles.innerItem} className="display-calendar-tooltip">
                                        <Typography
                                            color={colorStrings.secondaryMain}
                                            {...styles.triggerText}
                                            data-test-selector="calendarShipment"
                                            onClick={() => {
                                                setDisplayCalendarTooltip(!displayCalendarTooltip);
                                            }}
                                        >
                                            {translate("Calendar Shipment")}
                                        </Typography>

                                        {displayCalendarTooltip && (
                                            <StyledWrapper {...styles.availabilityTooltipWrapper} ref={tooltipRef}>
                                                {onCalendarSelected ? (
                                                    <>
                                                        <Typography
                                                            {...styles.availabilityTooltipTitle}
                                                            className="calendar-title"
                                                        >
                                                            {translate("On Calendar")}
                                                        </Typography>
                                                        <Typography
                                                            {...styles.availabilityTooltipText}
                                                            className="calendar-content"
                                                        >
                                                            {siteMessage("On_Calendar_Message")}
                                                        </Typography>
                                                        {calendarSettings?.showOnLink && (
                                                            <Link
                                                                target="_blank"
                                                                href={calendarSettings?.onLink}
                                                                rel="noreferrer"
                                                                {...styles.availabilityTooltipLink}
                                                                className="calendar-link"
                                                            >
                                                                {calendarSettings?.onLinkDescription}
                                                            </Link>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>
                                                        <Typography
                                                            {...styles.availabilityTooltipTitle}
                                                            className="calendar-title"
                                                        >
                                                            {translate("Off Calendar")}
                                                        </Typography>
                                                        <Typography
                                                            {...styles.availabilityTooltipText}
                                                            className="calendar-content"
                                                        >
                                                            {siteMessage("Off_Calendar_Message")}
                                                        </Typography>
                                                        {calendarSettings?.showOffLink && (
                                                            <Link
                                                                target="_blank"
                                                                href={calendarSettings?.offLink}
                                                                rel="noreferrer"
                                                                {...styles.availabilityTooltipLink}
                                                                className="calendar-link"
                                                            >
                                                                {calendarSettings?.offLinkDescription}
                                                            </Link>
                                                        )}
                                                    </>
                                                )}
                                            </StyledWrapper>
                                        )}
                                    </GridItem>

                                    <Select
                                        placeholder="Select"
                                        data-test-selector="checkoutReviewAndSubmit_shippingServiceSelect"
                                        onChange={e => {
                                            setOnCalendarSelected(e.currentTarget.value === "y");
                                        }}
                                        value={onCalendarSelected ? "y" : ""}
                                    >
                                        <option value="y">{translate("On Calendar")}</option>
                                        <option value="">{translate("Off Calendar")}</option>
                                    </Select>

                                    {(cart?.carriers?.length ?? 0) === 0 && (
                                        <Typography {...styles.invalidAddress}>
                                            {siteMessage("ReviewAndPay_InvalidAddressToShowCarrierServices")}
                                        </Typography>
                                    )}
                                </>
                            )}

                            <GridItem {...styles.innerItem}>
                                <Typography {...styles.shipMethodItemTitle}>{optionDescription}</Typography>
                            </GridItem>
                            <GridItem {...styles.innerItem}>
                                {optionDescription.toLowerCase() !== multipleShipmentMessageText.toLowerCase() ? (
                                    <Typography {...styles.shipMethodItemCopy}>
                                        {siteMessage("ReviewAndPay_ShipCompleteMessage")}
                                    </Typography>
                                ) : (
                                    <Typography {...styles.shipMethodItemCopy}>
                                        {siteMessage("ReviewAndPay_ShipAsAvailableMessage")}
                                    </Typography>
                                )}
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.shipMethodItem}>
                        <GridContainer {...styles.innerItemContainer}>
                            {onCalendar && onCalendarSelected && (
                                <GridItem {...styles.innerItem}>
                                    <Typography {...styles.shipDetailsHeading} as="h2">
                                        {translate("Estimated Shipping Date(s)")}
                                    </Typography>
                                </GridItem>
                            )}
                            {((onCalendar && !onCalendarSelected) || !onCalendar) && (
                                <GridItem {...styles.innerItem}>
                                    <Typography {...styles.shipDetailsHeading} as="h2">
                                        {translate("Preferred Delivery Date (Optional)")}
                                    </Typography>
                                </GridItem>
                            )}

                            {isLoading || !initialShipDay ? (
                                <StyledWrapper {...styles.loadingWrapper}>
                                    <LoadingSpinner></LoadingSpinner>
                                </StyledWrapper>
                            ) : (
                                <GridItem {...styles.innerItem} className="delivery-date-picker">
                                    <DatePicker
                                        {...styles.deliveryDatePicker}
                                        label={datePickerLabel}
                                        selectedDay={initialShipDay}
                                        onDayChange={handleRequestDeliveryDateChanged}
                                        dateTimePickerProps={{
                                            maxDate: addDays(
                                                initialShipDay,
                                                settingsCollection.cartSettings.maximumDeliveryPeriod,
                                            ),
                                            monthAriaLabel: translate("Request delivery date month"),
                                            dayAriaLabel: translate("Request delivery date day"),
                                            yearAriaLabel: translate("Request delivery date year"),
                                            tileDisabled: (tile: { date: Date | undefined }) => {
                                                if (
                                                    (!estimatedShippingValidDatesArr ||
                                                        estimatedShippingValidDatesArr.length === 0) &&
                                                    onCalendar
                                                ) {
                                                    if (tile.date && tile.date < initialShipDay) {
                                                        return true;
                                                    }
                                                    return false;
                                                }

                                                if (!onCalendar || !onCalendarSelected) {
                                                    if (tile.date && tile.date < initialShipDay) {
                                                        return true;
                                                    }
                                                    return false;
                                                }

                                                const isValid = estimatedShippingValidDatesArr.includes(
                                                    convertDateToDateOnlyString(tile.date),
                                                );

                                                return !isValid;
                                            },

                                            onClickDay: (date: Date) => {
                                                setDayCalendarClicked(true);
                                                setCustomShippingDetails({
                                                    estimateShipDateOrderChanged: false,
                                                });
                                            },
                                            ...styles.deliveryDatePicker?.dateTimePickerProps,
                                        }}
                                    />

                                    {estimateShipDateOrderChanged && onCalendarSelected && (
                                        <Typography {...styles.seeOrderLinesLabel}>
                                            {translate("See Order Lines")}
                                        </Typography>
                                    )}
                                </GridItem>
                            )}

                            {onCalendar && onCalendarSelected && (
                                <>
                                    <GridItem {...styles.innerItem}>
                                        <Typography {...styles.shippingScheduleLabel}>
                                            <Icon {...styles.infoIcon} />
                                            {translate("Your Shipping Schedule")}
                                        </Typography>
                                    </GridItem>
                                    <GridItem {...styles.innerItem}>
                                        <Typography as="p" size={14}>
                                            {cart?.properties.available_message}
                                        </Typography>
                                    </GridItem>
                                    <GridItem {...styles.innerItem}>
                                        <Typography as="p" size={14}>
                                            {siteMessage("ReviewAndPay_PreferredDeliveryDateMessage")}
                                        </Typography>
                                    </GridItem>
                                    <GridItem {...styles.innerItem}>
                                        <Typography as="p" size={14}>
                                            {siteMessage("ReviewAndPay_DisclaimerMessage")}
                                        </Typography>
                                    </GridItem>
                                </>
                            )}

                            {((onCalendar && !onCalendarSelected) || !onCalendar) && (
                                <GridItem {...styles.innerItem}>
                                    <Typography as="p" size={14}>
                                        {siteMessage("ReviewAndPay_PreferredDeliveryDateMessageOffCalendar")}
                                    </Typography>
                                </GridItem>
                            )}
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </StyledFieldSet>
        </StyledForm>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitShippingDetails) as any,
    definition: {
        group: "Checkout - Review & Submit",
        displayName: "Shipping Details",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
    },
};

export default widgetModule;
